import React, { Component } from "react";
import PropTypes from "prop-types";
import { default as imagesUploadModalPubSub } from "lib/PubSub/ImagesUploadModal/ImagesUploadModal";
import FolderPicker from "views/components/FolderPicker/FolderPicker";
import style from "./style.module.css";
import { noop, Logger, chainPromises, isEmpty } from "lib";
import { formatFileSize } from "lib/files/fileUtils";
import { getGifUploadData } from "lib/imageHelpers";
import { resizeImageForPreview } from "lib/resizeImageForPreview";
import { isVideo } from "lib/isVideo";
import {
  getVideoDurationFromFile,
  validateVideoFileCanDecode
} from "lib/videoHelpers";
import { isGif } from "lib/isGif";
import getFileExtension from "lib/getFileExtension";
import {
  ANIMATION_ASSET_TYPE_MAP,
  IMAGE_LIST_FOLDER_TYPES,
  MAX_MEDIA_DURATION
} from "lib/constants";
import { Modal } from "views/components";
import ImageJaggedMountainIcon from "views/components/icons/ImageJaggedMountainIcon";
import TickCircledIcon from "views/components/icons/TickCircledIcon";
import CrossCircledIcon from "views/components/icons/CrossCircledIcon";
import PATHS from "routes/paths";
import { Caret2Icon } from "views/components/icons";
import { isAudio } from "lib/isAudio";
import InfoAlert from "views/components/InfoAlert/InfoAlert";
import { compressFilesForUpload } from "lib/files/imageCompression";
import { validateSvgOrReject } from "lib/svg/validateSvg";

const acceptedFileTypes = ["png", "jpg", "jpeg", "svg", "gif"];

const initialState = (pages, folderId) => ({
  name: null,
  currentPage: pages.FOLDER_SELECTION,
  selectedFolderId: folderId || null,
  uploadingImages: {},
  uploadProgress: {},
  compressedFileCount: 0
});

class ImagesUploadModal extends Component {
  static PAGES = {
    FOLDER_SELECTION: "FOLDER_SELECTION",
    UPLOAD: "UPLOAD"
  };

  constructor(props) {
    super(props);

    this.getFolderSelectionContent = this.getFolderSelectionContent.bind(this);
    this.getUploadContent = this.getUploadContent.bind(this);
    this.handleChangeSelectedFolder =
      this.handleChangeSelectedFolder.bind(this);
    this.handleChangeCurrentPage = this.handleChangeCurrentPage.bind(this);
    this.handleFileSelect = this.handleFileSelect.bind(this);
    this.onImageUpload = this.onImageUpload.bind(this);
    this.getPageContents = this.getPageContents.bind(this);
    this.setFolderContext = this.setFolderContext.bind(this);
    this.processImage = this.processImage.bind(this);
    this.clearErrors = this.clearErrors.bind(this);
    this.getSubTextLabel = this.getSubTextLabel.bind(this);
    this.handleTabChangeOnUpload = this.handleTabChangeOnUpload.bind(this);
    this.handleOnClose = this.handleOnClose.bind(this);
    this.handleProgressUpdate = this.handleProgressUpdate.bind(this);
    this.startUploads = this.startUploads.bind(this);

    const fileTypes = this.props.acceptedFileTypes || acceptedFileTypes;

    this.state = {
      ...initialState(ImagesUploadModal.PAGES),
      acceptedFileTypes: fileTypes,
      acceptedFileExtensionList: `.${fileTypes.join(", .")}`,
      errors: null
    };
  }

  handleOnClose() {
    this.props.onClose({
      onSuccess: () => {
        if (this.props.isSmartImageTransfer)
          this.props.handleCloseTransferModal();
      }
    });
  }

  setFolderContext() {
    if (this.props.folderId) {
      this.handleChangeSelectedFolder(this.props.folderId);
    }
  }

  componentDidMount() {
    this.setFolderContext();
    this.props.onFetchUsersImageFolders();
  }

  componentDidUpdate(prevProps, prevState) {
    const {
      errors: propErrors,
      uploadErrors,
      isOpen,
      clearUploadErrors
    } = this.props;
    const { uploadingImages } = this.state;
    imagesUploadModalPubSub.onStateChange(prevState, this.state);

    if (isOpen !== prevProps.isOpen) {
      // when opening or closing the modal
      const isAtLeastOneUploading = Object.values(uploadingImages).some(
        upload => !upload.isFinished
      );
      // in the case that no uploads are still happening
      if (!isAtLeastOneUploading) {
        // return to the original state
        this.setState(
          initialState(ImagesUploadModal.PAGES, this.props.folderId)
        );
        this.setFolderContext();
      }
    }

    if (isOpen === false && prevProps.isOpen === true) {
      clearUploadErrors();
      this.setState({
        errors: null,
        uploadProgress: {},
        compressedFileCount: 0
      });
    }

    if (propErrors !== prevProps.errors) {
      this.setState({
        errors: propErrors
      });
    }

    if (uploadErrors !== prevProps.uploadErrors && isOpen === true) {
      const errorKeys = Object.keys(uploadErrors);
      const uploadingAnimationKeys = Object.keys(uploadingImages);

      let uploadingMedia = {};
      let errors = {};

      uploadingAnimationKeys.forEach(animation => {
        const animationKey = animation.replaceAll(" ", "-").toLowerCase();
        if (errorKeys.includes(animationKey)) {
          uploadingMedia[animation] = Object.assign(
            {},
            uploadingImages[animation],
            {
              isFinished: true,
              isError: true
            }
          );
          errors[animation] = {
            value:
              uploadErrors[animationKey][0].value +
              (this.props.isSmartImageTransfer
                ? " Your Smart ID has not been transferred"
                : "")
          };
        }
      });

      this.setState({
        uploadingImages: {
          ...this.state.uploadingImages,
          ...uploadingMedia
        },
        errors: {
          ...this.state.errors,
          ...errors
        }
      });
    }

    // Mobile files are handled via folder selection page first,
    // then processed when user clicks "Upload" button.

    // when upload modal is rendered over the transfer smart image modal
    // we should remove one modals background color
    const uploadModalDom = document.querySelector(
      `.${style.uploadModalDetails}`
    );
    if (
      this.props.isOpen &&
      this.props.isSmartImageTransfer &&
      uploadModalDom
    ) {
      uploadModalDom.parentNode.style.backgroundColor = "inherit";
    }
  }

  handleChangeCurrentPage(page) {
    this.setState({
      currentPage: page
    });
  }

  getPageContents(type) {
    const {
      onClose,
      isSmartImageTransfer = false,
      handleCloseTransferModal,
      handleSmartImageUploadClose = noop
    } = this.props;
    const getModalHeader = () => {
      if (isSmartImageTransfer)
        return (
          <div className={style.transferUploadHeader}>
            <div
              onClick={() =>
                onClose({ onSuccess: () => handleSmartImageUploadClose() })
              }
            >
              <Caret2Icon
                size="20px"
                color="#afb3b6"
                style={{
                  transform: "rotate(90deg)",
                  marginRight: "8px"
                }}
              />
            </div>
            Upload to...
          </div>
        );
      return "Upload to...";
    };

    const folderSelectionContent = {
      header: getModalHeader(),
      content: this.getFolderSelectionContent(),
      buttons: [
        {
          name: "Cancel",
          theme: "gray",
          onClick: () => this.handleOnClose()
        },
        {
          name: `Upload${isSmartImageTransfer ? " & Transfer" : ""}`,
          theme: "blueSolid",
          disabled: false,
          onClick: () => {
            if (this.props.mobileFiles) {
              const files = this.props.mobileFiles;
              const syntheticEvent = {
                target: { files },
                preventDefault: noop,
                stopPropagation: noop
              };
              this.handleFileSelect({ event: syntheticEvent });
            } else {
              this.triggerImageSelector();
            }
          }
        }
      ],
      buttonsClassName: isSmartImageTransfer
        ? style.transferButtons
        : style.selectButtons,
      modalStyle: isSmartImageTransfer
        ? style.transferModalDetails
        : style.uploadModalTo
    };

    const uploadContent = {
      header: "Upload details",
      content: this.getUploadContent(),
      buttons: [
        {
          name: "Hide",
          theme: "blueSolid",
          disabled: false,
          onClick: () => {
            onClose({
              onSuccess: () => {
                this.clearErrors();
                if (isSmartImageTransfer) handleCloseTransferModal();
              }
            });
          }
        }
      ],
      buttonsClassName: style.uploadButtons,
      modalStyle: isSmartImageTransfer
        ? style.transferDetailsModal
        : style.uploadModalDetails
    };

    switch (type) {
      case ImagesUploadModal.PAGES.FOLDER_SELECTION: {
        return folderSelectionContent;
      }
      case ImagesUploadModal.PAGES.UPLOAD: {
        return uploadContent;
      }
      default: {
        return folderSelectionContent;
      }
    }
  }

  handleChangeSelectedFolder(folderId) {
    const { selectedFolderId } = this.state;
    if (selectedFolderId !== folderId) {
      this.setState({
        selectedFolderId: folderId
      });
    }
  }

  stopBrowserDefaultBehaviour(e) {
    e.stopPropagation();
    e.preventDefault();
  }

  handleFlagFileAsFinishedUploading(fileName, resolve) {
    const { uploadingImages } = this.state;

    if (
      uploadingImages[fileName] &&
      uploadingImages[fileName].isFinished === false
    ) {
      this.setState({
        uploadingImages: Object.assign({}, uploadingImages, {
          [fileName]: Object.assign({}, uploadingImages[fileName], {
            isFinished: true
          })
        })
      });
      resolve();
    }
  }

  handleFlagFileAsErrorUploading(fileName, error, resolve) {
    this.setState(prevState => ({
      imageError: error,
      errors: {
        ...prevState.errors,
        [fileName]: { value: error }
      },
      uploadingImages: {
        ...prevState.uploadingImages,
        [fileName]: {
          ...prevState.uploadingImages[fileName],
          isFinished: true,
          isError: true
        }
      }
    }));
    resolve();
  }

  handleProgressUpdate(fileName, progress) {
    this.setState(prevState => ({
      uploadProgress: {
        ...prevState.uploadProgress,
        [fileName]: progress
      }
    }));
  }

  async handleFileSelect({ event }) {
    this.stopBrowserDefaultBehaviour(event);

    if (this.props.disabled) return;

    let files = Array.from(event.target.files);

    // Show upload page immediately so the user gets feedback during compression
    this.handleChangeCurrentPage(ImagesUploadModal.PAGES.UPLOAD);

    const result = await compressFilesForUpload(files);
    files = result.files;
    this.setState({ compressedFileCount: result.compressedCount });

    this.startUploads(files);
  }

  async startUploads(files) {
    const uploadingImages = files.reduce((previous, current, index) => {
      return Object.assign({}, previous, {
        [current.name]: {
          size: formatFileSize(files[index].size, 1),
          isFinished: false
        }
      });
    }, {});

    const uploadProgress = files.reduce((previous, current) => {
      return Object.assign({}, previous, {
        [current.name]: 0
      });
    }, {});

    this.setState({
      file: files[0],
      uploadingImages,
      imageError: null,
      uploadProgress
    });

    /* define function for creating promise containers for all uploads */
    const createImageProcessPromise = file => {
      return () =>
        new Promise(async resolve => {
          try {
            const svgResult = await validateSvgOrReject(file);
            if (!svgResult.ok) {
              this.handleFlagFileAsErrorUploading(
                file.name,
                svgResult.error,
                resolve
              );
              return;
            }
            this.processImage(file).then(
              result => {
                this.setState({ fileSrc: result });
                this.onImageUpload(file, () => {
                  this.handleFlagFileAsFinishedUploading(file.name, resolve);
                });
              },
              ({ error }) => {
                this.handleFlagFileAsErrorUploading(file.name, error, resolve);
              }
            );
          } catch {
            this.handleFlagFileAsErrorUploading(
              file.name,
              "We were not able to process your file.",
              resolve
            );
          }
        });
    };

    /* set up the promise containers for the upload */
    const uploadPromises = files.map(file => createImageProcessPromise(file));

    this.handleTabChangeOnUpload({ files });

    /* run the promises in chain */
    await chainPromises(uploadPromises);
  }

  async onImageUpload(file, onSuccess) {
    const { selectedFolderId } = this.state;
    const { onUploadUserTeamImage, onUploadImageToFolder, uploadOrigin } =
      this.props;
    this.setState({
      uploadingFileName: file.name,
      uploadingError: false
    });

    const uploadImage = !selectedFolderId
      ? onUploadUserTeamImage
      : onUploadImageToFolder;

    let uploadParams = {
      imageFile: file,
      folderId: selectedFolderId,
      onSuccess,
      onProgress: progress => this.handleProgressUpdate(file.name, progress)
    };

    // define what we should do when the presignedUrl is created
    const onPresignedUrlCreated = async id => {
      const isAnimation = isGif(file.name) || isVideo(file.name);
      const fileURL = await resizeImageForPreview(file);
      const getMediaType = () => {
        switch (true) {
          case isGif(file.name): {
            return ANIMATION_ASSET_TYPE_MAP.ANIMATION;
          }
          case isVideo(file.name): {
            return ANIMATION_ASSET_TYPE_MAP.VIDEO;
          }
          default: {
            return IMAGE_LIST_FOLDER_TYPES.IMAGE;
          }
        }
      };

      // build a placeholder media entry
      const media = {
        asset: file.name.toLowerCase(),
        createdAt: Date.now(),
        height: null,
        id,
        name: file.name.toLowerCase(),
        preview: "preview_placeholder.png",
        size: 75780,
        status: "ACTIVE",
        thumbnail: "thumb_placeholder.png",
        type: getMediaType(),
        updatedAt: Date.now(),
        width: null,
        duration: null
      };

      // build placeholder media entity
      const mediaEntity = {
        createdAt: Date.now(),
        media,
        mediaId: id,
        previewUrl: fileURL,
        thumbnailUrl: fileURL,
        updatedAt: Date.now(),
        url: fileURL,
        origin: uploadOrigin || "userTeamImage"
      };

      // add placeholder media entity to the store
      isAnimation
        ? this.props.addUploadingAnimationsPlaceholders({
            mediaPlaceholders: [mediaEntity],
            folderId: selectedFolderId
          })
        : this.props.addUploadingImagesPlaceholders({
            mediaPlaceholders: [mediaEntity],
            folderId: selectedFolderId
          });
    };

    if (isGif(file.name)) {
      const gifData = await getGifUploadData(file);
      uploadParams = {
        ...uploadParams,
        duration: gifData.duration,
        placeholderURL: window.URL.createObjectURL(file),
        uploadOrigin,
        onPresignedUrlCreated,
        noRefetch: selectedFolderId ? false : true,
        sortOptions: this.props.sortOptions,
        onProgress: progress => this.handleProgressUpdate(file.name, progress)
      };
    } else if (isVideo(file.name)) {
      let duration;
      try {
        duration = await getVideoDurationFromFile(file);
      } catch {
        this.setState(prevState => ({
          uploadingImages: Object.assign({}, prevState.uploadingImages, {
            [file.name]: Object.assign(
              {},
              prevState.uploadingImages[file.name],
              {
                isFinished: true,
                isError: true
              }
            )
          }),
          errors: {
            ...prevState.errors,
            [file.name]: {
              value:
                "Unable to read video metadata. The file may be corrupt or use an unsupported codec."
            }
          }
        }));
        return;
      }

      if (duration > MAX_MEDIA_DURATION) {
        this.setState(prevState => ({
          uploadingImages: Object.assign({}, prevState.uploadingImages, {
            [file.name]: Object.assign(
              {},
              prevState.uploadingImages[file.name],
              {
                isFinished: true,
                isError: true
              }
            )
          }),
          errors: {
            ...prevState.errors,
            [file.name]: {
              value: "Videos must be less than 5 minutes in duration"
            }
          }
        }));
        // return early to prevent animation upload
        return;
      }

      uploadParams = {
        ...uploadParams,
        duration,
        placeholderURL: window.URL.createObjectURL(file),
        uploadOrigin,
        onPresignedUrlCreated,
        noRefetch: selectedFolderId ? false : true,
        sortOptions: this.props.sortOptions,
        onProgress: progress => this.handleProgressUpdate(file.name, progress)
      };
    } else if (isAudio(file.name)) {
      const getAudioDuration = () =>
        new Promise(resolve => {
          let audio = document.createElement("audio");
          audio.preload = "metadata";
          audio.onloadedmetadata = () => {
            window.URL.revokeObjectURL(audio.src);
            // Floor the duration to prevent fractional values sent in request (in milliseconds)
            resolve(Math.floor(audio.duration * 1000));
          };
          audio.onerror = () => {
            // If audio metadata can't be loaded, resolve with 0 duration
            window.URL.revokeObjectURL(audio.src);
            resolve(0);
          };
          audio.src = window.URL.createObjectURL(file);
        });

      const duration = await getAudioDuration();

      if (duration > MAX_MEDIA_DURATION) {
        const durationMinutes = Math.round((duration / 1000 / 60) * 10) / 10;
        const errorMessage = `Audio file is too long (${durationMinutes} minutes). Please upload audio files that are 5 minutes or shorter.`;

        // Set the error state immediately to show user feedback
        this.setState({
          errors: {
            ...this.state.errors,
            [file.name]: {
              value: errorMessage
            }
          },
          uploadingImages: {
            ...this.state.uploadingImages,
            [file.name]: {
              ...this.state.uploadingImages[file.name],
              isFinished: true,
              isError: true
            }
          }
        });
        return; // Stop the upload process
      }

      uploadParams = {
        ...uploadParams,
        duration,
        placeholderURL: window.URL.createObjectURL(file),
        uploadOrigin,
        onPresignedUrlCreated,
        noRefetch: selectedFolderId ? false : true,
        sortOptions: this.props.sortOptions,
        onProgress: progress => this.handleProgressUpdate(file.name, progress)
      };
    }

    uploadImage(uploadParams);
  }

  processImage(file) {
    const fileExtension = getFileExtension(file.name);

    if (!this.state.acceptedFileTypes.includes(fileExtension)) {
      const errorMessage = isAudio(file.name)
        ? "Audio files must be in MP3 or WAV format."
        : "Images must be in PNG, JPG, GIF, MP4, or MOV format.";
      return Promise.reject({
        error: errorMessage
      });
    }

    const promise = new Promise((resolve, reject) => {
      const errorCallback = msg => () => {
        Logger.warn(msg);
        return reject({ error: "We were not able to process your file." });
      };

      const validateFileAsUsableAsset = fileSrc => {
        if (
          fileSrc.includes("video/mp4") ||
          fileSrc.includes("video/quicktime")
        ) {
          validateVideoFileCanDecode(file).then(() => {
            Logger.info({ app: "Video processed successfully" });
            resolve(fileSrc);
          }, errorCallback("Video onload error"));
        } else if (fileSrc.includes("audio/")) {
          // Handle audio files - no validation needed, just resolve
          Logger.info({ app: "Audio processed successfully" });
          resolve(fileSrc);
        } else {
          // Handle image files
          const image = new Image();
          image.onerror = errorCallback("Image onload error");
          image.onload = () => {
            Logger.info({ app: "Image processed successfully" });
            resolve(fileSrc);
          };
          image.src = fileSrc;
        }
      };

      const reader = new FileReader();
      reader.onerror = errorCallback("File Reader onload error");

      reader.onload = ({ target }) => validateFileAsUsableAsset(target.result);
      reader.readAsDataURL(file);
    });

    return promise;
  }

  handleTabChangeOnUpload(files) {
    const { uploadOrigin } = this.props;
    const isAnimationUpload = ["teamAnimation", "userTeamAnimation"].includes(
      uploadOrigin
    );

    // return safely if not an animation upload or
    // not located in the editor
    if (!isAnimationUpload || !PATHS.isEditorPath(window.location.pathname))
      return;

    const { files: _files } = files;
    const subTab =
      uploadOrigin === "teamAnimation" ? "TEAM_ANIMATIONS" : "MY_ANIMATIONS";

    this.props.handleUploadTabChange({
      files: _files,
      subTab,
      folderId: this.state.selectedFolderId
    });
  }

  triggerImageSelector() {
    document.getElementById("ImageUpload").click();
  }

  clearErrors() {
    const { clearUploadErrors = noop } = this.props;

    this.setState({
      errors: null,
      uploadProgress: {}
    });
    clearUploadErrors();
  }

  getSubTextLabel() {
    const { destinationName = "My Images", isSmartImageTransfer } = this.props;
    let uploadType = "images";
    if (destinationName === "My Videos") {
      uploadType = "videos";
    } else if (destinationName === "My Audio") {
      uploadType = "audio files";
    }
    let message = `Your ${uploadType} are now uploading and will appear as they are completed.`;

    if (isSmartImageTransfer) {
      message +=
        " Your Smart ID will automatically be transferred to the new image.";
    }

    return message;
  }

  getUploadContent() {
    const {
      imageFolders,
      destinationName = "My Images",
      isSmartImageTransfer
    } = this.props;
    const {
      uploadingImages,
      selectedFolderId,
      errors,
      uploadProgress,
      compressedFileCount
    } = this.state;

    let selectedFolderName = destinationName;
    if (selectedFolderId !== null && !isEmpty(imageFolders)) {
      const selectedFolder = imageFolders.find(
        folder => folder.id === selectedFolderId
      );
      if (selectedFolder) {
        selectedFolderName = selectedFolder.name;
      }
    }

    const getUploadSuccessIcon = (image, fileName) => {
      if (!image.isFinished && !image.isError) {
        const progress = uploadProgress[fileName] || 0;
        return (
          <div className={style.uploadProgressContainer}>
            <div className={style.uploadProgressBar}>
              <div
                className={style.uploadProgressBarFill}
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        );
      }
      if (image.isFinished && image.isError) {
        return <CrossCircledIcon color="#e64826" size="14px" />;
      }
      if (image.isFinished && !image.isError) {
        return <TickCircledIcon size="14px" color="#00bd62" />;
      }
    };

    return (
      <div
        className={
          isSmartImageTransfer ? style.tansferContent : style.uploadContent
        }
      >
        <div className={style.uploadContentSubText}>
          {this.getSubTextLabel()}
        </div>
        <div className={style.uploadUploadingTo}>
          Uploading to:{" "}
          <span className={style.uploadLocation}>{selectedFolderName}</span>
        </div>
        <div
          className={style.uploadTable}
          style={{ marginBottom: compressedFileCount > 0 ? "13px" : "32px" }}
        >
          {Object.keys(uploadingImages).map(key => (
            <React.Fragment key={key}>
              <div className={style.uploadTableRow}>
                <div className={style.uploadRowLeft}>
                  <div className={style.uploadImageIconWrapper}>
                    <ImageJaggedMountainIcon
                      isTabVersion={false}
                      size="24px"
                      color="#9a9b9d"
                    />
                  </div>
                  <div className={style.uploadRowText}>{key}</div>
                </div>
                <div className={style.uploadRowRight}>
                  <div>{uploadingImages[key].size}</div>
                  <div>{getUploadSuccessIcon(uploadingImages[key], key)}</div>
                </div>
              </div>
              {errors && errors[key] && (
                <div className={style.uploadError}>{errors[key].value}</div>
              )}
            </React.Fragment>
          ))}
        </div>
        {compressedFileCount > 0 && (
          <InfoAlert
            theme="blue"
            hasIcon={true}
            className={style.infoText}
            iconSize="36px"
          >
            {compressedFileCount === 1
              ? "You have 1 large file that has been"
              : `You have ${compressedFileCount} large files that have been`}{" "}
            compressed to save upload time and space. Images have been optimized
            and may be resized.
          </InfoAlert>
        )}
      </div>
    );
  }

  getFolderSelectionContent() {
    const {
      imageFolders,
      destinationName = "My Images",
      isSmartImageTransfer = false
    } = this.props;
    const { selectedFolderId } = this.state;

    return (
      <FolderPicker
        folders={imageFolders}
        onClick={this.handleChangeSelectedFolder}
        destinationName={destinationName}
        selectedFolderId={selectedFolderId}
        className={isSmartImageTransfer ? style.smartImageTransferContent : ""}
      />
    );
    // return (
    //   <div className={style.folderSelectionContent}>
    //     <div
    //       className={style.folderSelectionContentItem}
    //       data-is-selected={!selectedFolderId}
    //       onClick={() => this.handleChangeSelectedFolder(null)}
    //     >
    //       <div className={style.uploadFolderIconWrapper}>
    //         <FolderWideIcon size="22px" color="#9a9b9d" />
    //       </div>
    //       <div className={style.folderSelectionFolderTitle}>
    //         {destinationName}
    //       </div>
    //       {!selectedFolderId && (
    //         <div className={style.folderSelectionContentTickWrapper}>
    //           <TickIcon hasCircle={false} color={style.easilBlueDeep} />
    //         </div>
    //       )}
    //     </div>
    //     {imageFolders.map(folder => {
    //       const isSelected = selectedFolderId === folder.id;
    //       return (
    //         <div
    //           key={folder.id}
    //           className={style.folderSelectionContentItem}
    //           data-is-sub={true}
    //           data-is-selected={isSelected}
    //           onClick={() => this.handleChangeSelectedFolder(folder.id)}
    //         >
    //           <div className={style.uploadFolderIconWrapper}>
    //             <FolderWideIcon size="22px" color="#9a9b9d" />
    //           </div>
    //           <div className={style.uploadFolderName}>{folder.name}</div>
    //           {isSelected && (
    //             <div className={style.folderSelectionContentTickWrapper}>
    //               <TickIcon hasCircle={false} color={style.easilBlueDeep} />
    //             </div>
    //           )}
    //         </div>
    //       );
    //     })}
    //   </div>
    // );
  }

  render() {
    const { isOpen, isSmartImageTransfer = false } = this.props;
    const { currentPage } = this.state;

    const pageContent = this.getPageContents(currentPage);

    return (
      <Modal
        isOpen={isOpen}
        contentLabel="Preview"
        onRequestClose={() => this.handleOnClose()}
        header={pageContent.header}
        className={pageContent.modalStyle}
        buttons={pageContent.buttons}
        buttonsClassName={pageContent.buttonsClassName}
      >
        {pageContent.content}
        <input
          type="file"
          accept={this.state.acceptedFileExtensionList}
          id={"ImageUpload"}
          className={style.inputfile}
          onChange={e => this.handleFileSelect({ event: e })}
          multiple={!isSmartImageTransfer}
        />
      </Modal>
    );
  }
}

ImagesUploadModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onUploadUserTeamImage: PropTypes.func,
  imageFolders: PropTypes.array
};

export default ImagesUploadModal;
